import { ParentWindowService } from "@parent-window/parent-window";
import { SDK } from "@sdk";
import {
  iChatBot,
  iChatBotFlowConfig,
  iMessageNode,
} from "@sdk/services/chat-bot-models";
import { iCampaign, TriggerConditionPresets } from "@sdk/services/models";
import { push } from "connected-react-router";
import { initLocalCampaignConversation } from "helpers/init-local-conversation";
import find from "lodash/find";
import last from "lodash/last";
import { store } from "store/store";
import { getUrlData, UrlDataObj } from "utils/get-url-data";
import { justWait } from "utils/just-wait";
import {
  selectConversationsMapAll,
  selectConversationsQuery,
} from "../conversations/conversations.selectors";
import { setConversationsQueryList } from "../conversations/conversations.slice";
import { selectSession, selectWidget } from "../session/session.selectors";

import {
  addCampaignEvent,
  clearActiveCampaign,
  setActiveCampaign,
} from "./app-state.actions";
import { selectCampaignState } from "./app-state.selectors";
import { GenerateSimpleCampaign } from "./helpers/generate-simple-campaign";
import { iActivatedMessageCampaign } from "@sdk/services/activated-message-campaigns-models";
import { getAllActiveUsers } from "../users/users.helpers";
import { GenerateSimpleCampaignMessage } from "helpers/local-conversation-generator";

export class CampaignsController {
  public static INIT_TIME = new Date().getTime();
  public static VISIT_TIMEOUT = 30 * 60 * 1000;

  public static CAMPAIGN_TRIGGERED = false;

  public static READY = false;
  public static STARTED = false;
  public static ActiveMessageCampaigns: iActivatedMessageCampaign[] = [];

  public static LoadedChatBotFirstNodes: {
    [chatBotId: string]: Partial<iMessageNode>;
  } = {};

  /**
   * Checks if the current page URL should allow auto-trigger based on whiteListedPages and blackListedPages configuration
   */
  static shouldAllowAutoTrigger(
    currentUrl?: string,
    autoTriggerConfig?: {
      whiteListedPages?: string[];
      blackListedPages?: string[];
    }
  ): boolean {
    if (!currentUrl) {
      const campaignState = selectCampaignState(store.getState());
      const currentVisit = last(campaignState.eventTraces.pagesVisits);
      currentUrl = currentVisit?.url || "";
    }
    // console.log("autoTriggerConfig", autoTriggerConfig, currentUrl);
    if (!autoTriggerConfig) {
      return true; // If no config, allow by default
    }

    const { whiteListedPages, blackListedPages } = autoTriggerConfig;

    // Check blacklist first - if current URL matches any blacklisted page, deny
    if (blackListedPages && blackListedPages.length > 0) {
      for (const blacklistedPage of blackListedPages) {
        if (currentUrl.includes(blacklistedPage)) {
          return false;
        }
      }
    }

    // Check whitelist - if whitelist is defined, current URL must match at least one whitelisted page
    if (whiteListedPages && whiteListedPages.length > 0) {
      let isWhitelisted = false;
      for (const whitelistedPage of whiteListedPages) {
        if (currentUrl.includes(whitelistedPage)) {
          isWhitelisted = true;
          break;
        }
      }
      return isWhitelisted;
    }

    // If no whitelist is defined, allow (blacklist was already checked above)
    return true;
  }

  public static async init() {
    if (!this.STARTED) {
      this.STARTED = true;
      await justWait(400); // Not sure why i added this
      await this.loadActiveMessageCampaigns();
      this.initTimer();
      this.reStoreCampaignState();
      this.READY = true;
    }
  }

  public static async loadActiveMessageCampaigns() {
    const session = selectSession(store.getState());
    const isNewSession =
      (session?.metaData?.createdTime || 0) + 5000 > Date.now();
    const activeMessageCampaigns = isNewSession
      ? []
      : await SDK.getActiveMessageCampaigns();
    this.ActiveMessageCampaigns = activeMessageCampaigns;
  }

  public static async onMessageCampaignActivated(messageCampaignId: string) {
    const campaign = this.ActiveMessageCampaigns.find(
      (item) => item.id === messageCampaignId
    );
    // Mark Campaign as seen locally and remotely
    if (campaign && !campaign?.isSeen) {
      campaign.isSeen = true;
      await SDK.markMessageCampaignAsSeen(messageCampaignId);
    }
  }

  public static initTimer() {
    let intervalCount = 0;
    const interval = setInterval(async () => {
      await this.run();
      if (intervalCount > 3) {
        clearInterval(interval);
      }
      intervalCount++;
    }, 5000);
  }

  public static addEvent(pageData: {
    url: string;
    title: string;
    visitTimestamp: number;
    referer: string;
  }) {
    store.dispatch(
      addCampaignEvent({
        ...pageData,
        pageData: getUrlData(pageData.url),
      })
    );
    if (this.STARTED) {
      this.run();
    }
  }

  public static async getChatbotFirstNodeConfig(
    widgetId: string,
    chatBotId: string
  ) {
    if (!this.LoadedChatBotFirstNodes[chatBotId]) {
      const startNode = await SDK.getChatBotFirstNodeConfiguration(
        widgetId!,
        chatBotId
      );
      this.LoadedChatBotFirstNodes[chatBotId] = startNode;
    }
    const startNode = this.LoadedChatBotFirstNodes[chatBotId];

    return startNode;
  }

  public static async getChatbotCampaignConfig(
    widgetId: string,
    chatBotId: string
  ) {
    const startNode = await this.getChatbotFirstNodeConfig(
      widgetId!,
      chatBotId
    );
    return {
      text: startNode?.text || "",
      // quickReplies: startNode.quickButtons.map((item) => ({
      //   label: item.label,
      //   action: "MESSAGE",
      //   data: {},
      // })),
      coverImage: undefined,
      chatBotId: chatBotId,
      options: {
        additionalMessageData: {
          botContext: startNode,
        },
      },
    };
  }

  public static async getRegisteredCampaigns() {
    const campaignState = selectCampaignState(store.getState());
    const widget = selectWidget(store.getState())!;
    const session = selectSession(store.getState())!;
    let registeredCampaigns = [
      ...(widget?.configurations.conversationFlow.campaigns || []),
    ];

    const activeMessageCampaigns: iCampaign[] = [];

    for (const item of this.ActiveMessageCampaigns) {
      if (item.messageConfig?.chatbotId) {
        const chatBotConfig = await CampaignsController.getChatbotCampaignConfig(
          widget.id!,
          item.messageConfig?.chatbotId
        );
        // console.log("chatBotConfig", chatBotConfig);
        activeMessageCampaigns.push({
          ...GenerateSimpleCampaign({
            id: item.id!,
            text: chatBotConfig.text,
            quickReplies: undefined,
            coverImage: chatBotConfig.coverImage,
            chatBotId: item.messageConfig?.chatbotId,
            messageCampaignId: item.id,
            options: chatBotConfig.options,
          }),
        });
      } else {
        activeMessageCampaigns.push({
          ...GenerateSimpleCampaign({
            id: item.id!,
            text: item.messageConfig?.message || "",
            quickReplies: undefined,
            coverImage: undefined,
            chatBotId: item.messageConfig?.chatbotId,
            messageCampaignId: item.id,
            options: {
              // Todo
            },
          }),
        });
      }
    }

    // console.log("activeMessageCampaigns", activeMessageCampaigns);
    // Add Activated Message Campaigns
    registeredCampaigns = [...registeredCampaigns, ...activeMessageCampaigns];

    const greetingMessageConfig =
      widget?.configurations.conversationFlow.greetingsMessage;
    if (greetingMessageConfig && greetingMessageConfig.autoTrigger) {
      const autoTriggerConfig = greetingMessageConfig.autoTriggerConfig;

      // Get current page URL for auto-trigger validation
      const currentVisit = last(campaignState.eventTraces.pagesVisits);
      const currentUrl = currentVisit?.url || "";

      // Check if auto-trigger should be allowed based on page restrictions
      if (this.shouldAllowAutoTrigger(currentUrl, autoTriggerConfig)) {
        if (greetingMessageConfig.chatBotId) {
          const chatBotConfig = await CampaignsController.getChatbotCampaignConfig(
            widget.id!,
            greetingMessageConfig.chatBotId
          );

          if (chatBotConfig?.text) {
            registeredCampaigns.push(
              GenerateSimpleCampaign(
                {
                  id: "DEFAULT_GREETINGS_CHAT_BOT",
                  text: chatBotConfig.text,
                  // quickReplies: startNode.quickButtons.map((item) => ({
                  //   label: item.label,
                  //   action: "MESSAGE",
                  //   data: {},
                  // })),
                  coverImage: chatBotConfig.coverImage,
                  chatBotId: chatBotConfig.chatBotId,
                  options: chatBotConfig.options,
                } // autoTriggerConfig.coverImage
              )
            );
          }
        } else {
          registeredCampaigns.push(
            GenerateSimpleCampaign({
              id: "DEFAULT_GREETINGS",
              text: greetingMessageConfig.text,
              quickReplies: greetingMessageConfig.options,
              coverImage: greetingMessageConfig.coverImage,
            })
          );
        }
      }
    }
    //  Add Multi Channel Default Greetings
    if (
      widget.configurations.multiChannelConfig?.isEnabled &&
      widget.configurations.multiChannelConfig?.greetingsMessage &&
      widget.configurations.multiChannelConfig?.autoTriggerGreetings
    ) {
      // Get current page URL for multi-channel auto-trigger validation
      const currentVisit = last(campaignState.eventTraces.pagesVisits);
      const currentUrl = currentVisit?.url || "";

      // For multi-channel, we don't have autoTriggerConfig, so we allow by default
      // but we still need to check if autoTriggerGreetings is enabled
      if (currentUrl) {
        registeredCampaigns.push(
          GenerateSimpleCampaign({
            id: "DEFAULT_MULTI_CHANNEL_GREETINGS",
            text: widget.configurations.multiChannelConfig.greetingsMessage,
          })
        );
      }
    }
    if (
      session?.chatInvite &&
      session?.chatInvite.invitedTime + 60 * 60 * 1000 > Date.now()
    ) {
      registeredCampaigns.push(
        GenerateSimpleCampaign({
          id: "INVITE_MESSAGE",
          text: session.chatInvite.message,
          quickReplies: undefined,
          coverImage: undefined,
          chatBotId: undefined,
          options: {
            additionalMessages: session.chatInvite.subsequentMessages,
            userId: session.chatInvite.invitedBy,
          },
        })
      );
    }
    return registeredCampaigns;
  }

  public static async run() {
    const campaignState = selectCampaignState(store.getState());
    const conversations = selectConversationsQuery("min")(store.getState())
      .list;
    const openConversation = find(conversations, { status: "OPEN" });
    const allActiveUsers = await getAllActiveUsers(store);
    const onlineUsers = find(allActiveUsers, { "metaData.available": true });

    // console.log(
    //   "Run Campaign",
    //   campaignState.activatedCampaign,
    //   this.CAMPAIGN_TRIGGERED,
    //   openConversation,
    //   allActiveUsers
    // );

    if (campaignState.activatedCampaign) {
      return;
    }
    if (this.CAMPAIGN_TRIGGERED) {
      return;
    }

    if (openConversation) {
      return;
    }
    // if ((conversations || []).length > 0) {
    //   return;
    // }
    // if (!onlineUsers) {
    //   return;
    // }

    const registeredCampaigns = await this.getRegisteredCampaigns();

    const currentVisit = last(campaignState.eventTraces.pagesVisits);

    for (const campaign of registeredCampaigns) {
      const triggerCondition = campaign.data.triggerCondition;
      if (triggerCondition.preset === TriggerConditionPresets.NONE) {
        // console.log(
        //   "TriggerConditionPresets.NONE",
        //   TriggerConditionPresets.NONE
        // );
        const DELAY = 5000;
        if (
          triggerCondition.data &&
          triggerCondition.data.forUtmTags &&
          triggerCondition.data.forUtmTags.length > 0
        ) {
          let conditionMatchedOnce = false;

          let conditionFailed = false;
          // console.log('campaignState.eventTraces.pagesVisits', campaignState.eventTraces.pagesVisits);
          for (const pageVisit of campaignState.eventTraces.pagesVisits) {
            if (pageVisit.pageData) {
              for (const condition of triggerCondition.data.forUtmTags) {
                if (
                  pageVisit.pageData.queryParams[condition.tag] ===
                  condition.value
                ) {
                  conditionMatchedOnce = true;
                } else {
                  conditionFailed = true;
                }
              }
            }
          }

          //* AND Condition ix executed as logic to trigger the campaign

          if (
            conditionMatchedOnce &&
            !conditionFailed &&
            currentVisit &&
            currentVisit.visitTimestamp + DELAY < new Date().getTime()
          ) {
            // *Trigger the campaign
            await this.triggerCampaignWithCampaignId(campaign.id);
            break;
          }
        } else if (
          triggerCondition.data &&
          (triggerCondition.data.pageContains?.length || 0) > 0
        ) {
          if (
            currentVisit &&
            currentVisit.visitTimestamp + DELAY < new Date().getTime() &&
            currentVisit?.url.includes(triggerCondition.data.pageContains || "")
          ) {
            // *Trigger the campaign
            await this.triggerCampaignWithCampaignId(campaign.id, {
              isTemporary: true,
            });
            break;
          }
        } else {
          // No UTM Conditions are given or not pageContains is given
          // console.log("currentVisit", currentVisit);
          if (
            currentVisit &&
            currentVisit.visitTimestamp + DELAY < new Date().getTime()
          ) {
            // *Trigger the campaign
            await this.triggerCampaignWithCampaignId(campaign.id);
            break;
          }
        }
      } else if (
        triggerCondition.preset ===
        TriggerConditionPresets.ON_EVENT_RECEIVED_FROM_PARENT
      ) {
        // Todo: No need to handle it now
      } else if (
        triggerCondition.preset ===
        TriggerConditionPresets.ON_NAVIGATION_TO_SECOND_PAGE
      ) {
        if (campaignState.eventTraces.pagesVisits.length > 1) {
          // *Trigger the campaign
          await this.triggerCampaignWithCampaignId(campaign.id);
          break;
        }
      }
    }
  }

  public static async triggerCampaignWithCampaignId(
    campaignId: string,
    options: {
      userId?: string;
      muteNotifications?: boolean;
      isTemporary?: boolean;
    } = {}
  ) {
    const registeredCampaigns = await this.getRegisteredCampaigns();
    const campaign = find(registeredCampaigns, { id: campaignId });
    if (campaign) {
      this.triggerCampaign(campaign, options);
    }
  }

  public static async triggerChatbotAsCampaign(chatbotId: string) {
    const widget = selectWidget(store.getState())!;
    const chatBotConfig = await CampaignsController.getChatbotCampaignConfig(
      widget.id!,
      chatbotId
    );

    const campaign = GenerateSimpleCampaign(
      {
        id: "DEFAULT_GREETINGS_CHAT_BOT",
        text: chatBotConfig.text || "",
        // quickReplies: startNode.quickButtons.map((item) => ({
        //   label: item.label,
        //   action: "MESSAGE",
        //   data: {},
        // })),
        coverImage: chatBotConfig.coverImage,
        chatBotId: chatbotId,
        options: chatBotConfig.options,
      } // autoTriggerConfig.coverImage
    );

    await this.dismissAllCampaigns();
    await this.triggerCampaign(campaign);
  }

  public static async triggerCampaign(
    campaign: iCampaign,
    options: {
      userId?: string;
      muteNotifications?: boolean;
      isTemporary?: boolean;
    } = {}
  ) {
    if (campaign.id === "DEFAULT_MULTI_CHANNEL_GREETINGS") {
      ParentWindowService.sendNewMessageNotification(
        GenerateSimpleCampaignMessage(
          {
            message: campaign.data.message,
            conversationId: "",
          },
          store
        ),
        {
          muteNotification: true,
          resetCount: true,
        }
      );
      this.CAMPAIGN_TRIGGERED = true;
      return;
    }
    // console.log("triggerCampaign", campaign, options);
    // Todo: If Multi Channel is enabled, then we need to trigger the campaign differently
    const { campaignUser } = await initLocalCampaignConversation(
      campaign,
      options
    )(store);
    if (!options.isTemporary) {
      store.dispatch(
        setActiveCampaign({ campaignId: campaign.id, campaignUser })
      );
    }
    this.CAMPAIGN_TRIGGERED = true;
    if (campaign.data.messageCampaignId) {
      this.onMessageCampaignActivated(campaign.data.messageCampaignId);
    }
  }

  public static dismissAllCampaigns(withoutNavigation?: boolean) {
    const allConversationIds =
      selectConversationsMapAll(store.getState())?.list || [];
    const newConversationList = allConversationIds.filter(
      (id) => !id.includes("TEMP")
    );
    const widget = selectWidget(store.getState());

    store.dispatch(
      setConversationsQueryList({
        query: "all",
        list: newConversationList,
      })
    );
    // Todo: Go to home screen
    if (!withoutNavigation) {
      store.dispatch(push(`/`));
    }

    // Send Signal to Parent Window to remove the campaign
    ParentWindowService.dismissChatBubble();
    this.CAMPAIGN_TRIGGERED = false;
  }

  public static async reStoreCampaignState() {
    let campaignState = selectCampaignState(store.getState());
    // const conversations = selectAllConversations(store.getState());
    const conversations = selectConversationsQuery("min")(store.getState())
      .list;
    const existingActiveConversation = find(
      conversations,
      (conversation) =>
        conversation.status === "OPEN" && !conversation.isTemporary
    );
    const session = selectSession(store.getState())!;

    if (
      campaignState &&
      !campaignState.activatedCampaign?.includes(session.organizationId)
    ) {
      store.dispatch(clearActiveCampaign({}));
      campaignState = selectCampaignState(store.getState());
    }

    if (session) {
      if (
        session.chatInvite &&
        session.chatInvite.invitedTime + 60 * 60 * 1000 > Date.now()
      ) {
        this.triggerCampaignWithCampaignId("INVITE_MESSAGE", {
          muteNotifications: true,
          userId: campaignState.campaignUser,
        });
        return;
      }
    }
    if (campaignState.activatedCampaign) {
      // console.log('campaignState.activatedCampaign', campaignState.activatedCampaign)
      const availableCampaigns = await this.getRegisteredCampaigns();
      if (
        availableCampaigns.find(
          (item) => item.id === campaignState.activatedCampaign
        )
      ) {
        if (
          (campaignState.activatedTimestamp || 0) + 60 * 60 * 1000 >
            Date.now() &&
          !existingActiveConversation
        ) {
          if (!this.CAMPAIGN_TRIGGERED) {
            this.triggerCampaignWithCampaignId(
              campaignState.activatedCampaign,
              {
                muteNotifications: true,
                userId: campaignState.campaignUser,
              }
            );
          }
        } else {
          // Reset Active Campaigns
          store.dispatch(
            setActiveCampaign({ campaignId: "", campaignUser: "" })
          );
        }
      } else {
        // Reset Active Campaigns
        store.dispatch(setActiveCampaign({ campaignId: "", campaignUser: "" }));
      }
    }
  }
}
